// src/App.tsx

import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { PrivateRoute } from './router/PrivateRouter';
import { RoleProtectedRoute } from './router/RoleProtectedRoute';
import { useReportEditorKey } from './router/useReportEditorKey';
import { MainLayout } from './layouts';
import { getHomePath } from './shared/utils/homePath';
import LoginPage from './pages/LoginPage';
import VerifyTokenPage from './pages/VerifyTokenPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AzureCallbackPage from './pages/AzureCallbackPage';
import LaunchPage from './pages/LaunchPage';
import NotFoundPage from './pages/NotFoundPage';
import { importWithStaleChunkRecovery } from './shared/utils/dynamicImport';

// Lazy-loaded pages — each gets its own chunk, downloaded on first navigation.
// Wrapped in importWithStaleChunkRecovery so that, after a deploy invalidates
// the previous chunk hashes, the page reloads instead of surfacing a
// "Failed to fetch dynamically imported module" TypeError to React/Sentry.
const ReportsDashboardPageV2 = lazy(() => importWithStaleChunkRecovery(() => import('./pages/ReportsDashboardPageV2')));
const ReportEditPageV2 = lazy(() => importWithStaleChunkRecovery(() => import('./pages/ReportEditPageV2')));
const SettingsPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/SettingsPage')));
const BillingSettingsPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/BillingSettingsPage')));
const TemplatePage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/TemplatePage')));
const SuperPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/SuperPage')));
const RadiologistInsightsPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/RadiologistInsightsPage')));
const GroupReportsPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/GroupReportsPage')));
// V2 (Gemini, sin timestamps). La V1 (Chirp) queda obsoleta en pages/BatchReportingPage.
const BatchReportingPageV2 = lazy(() => importWithStaleChunkRecovery(() => import('./pages/BatchReportingPageV2')));
const SharedDraftPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/SharedDraftPage')));
const AdminPanelPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/AdminPanelPage')));
const OrgAdminPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/OrgAdminPage')));
const MicPage = lazy(() => importWithStaleChunkRecovery(() => import('./pages/MicPage')));

import { BatchEditProvider } from './shared/context/BatchEditContext';
import { SettingsProvider } from './shared/context/SettingsContext';
import { ToastProvider } from './shared/context/ToastContext';
import { ThemeProvider } from './shared/context/ThemeContext';
import { BrandProvider } from './shared/context/BrandContext';
import { MicSessionProvider } from './shared/context/MicSessionContext';
import { useAuth } from './shared/context/AuthContext';
import { useDesktopDeepLink } from './shared/hooks/useDesktopDeepLink';
import LoadingSpinner from './shared/components/ui/LoadingSpinner';
import { SessionExpiryManager } from './shared/components/ui/SessionExpiryModal/SessionExpiryManager';
import { ConsentGateManager } from './shared/components/legal/ConsentGate/ConsentGateManager';


// Keys the editor by its route (see useReportEditorKey).
function KeyedReportEditPageV2() {
  const editorKey = useReportEditorKey();
  return <ReportEditPageV2 key={editorKey} />;
}


function App() {
  const location = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();
  useDesktopDeepLink();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/' || location.pathname === '/verify-email' || location.pathname === '/azure/auth/callback' || location.pathname === '/forgot-password' || location.pathname === '/reset-password' || location.pathname === '/launch' || location.pathname === '/admin';
  const isMicPage = location.pathname.startsWith('/mic/');
  const isOrgAdminPage = location.pathname === '/org' || location.pathname.startsWith('/org/');

  // Lista de rutas válidas para detectar 404
  const validRoutes = ['/reports', '/reports/v2/edit', '/super', '/accounts', '/users', '/settings', '/billing', '/templates', '/my-insights', '/group-reports', '/batch', '/issues', '/admin', '/org', '/login', '/verify-email', '/forgot-password', '/reset-password', '/azure/auth/callback', '/launch', '/'];
  const isNotFoundPage = !validRoutes.includes(location.pathname) && !location.pathname.startsWith('/reports/v2/edit/') && !location.pathname.startsWith('/batch/edit/') && !location.pathname.startsWith('/drafts/shared/') && !isMicPage && !isOrgAdminPage;

  // Mostrar loading mientras se verifica la autenticación
  if (isLoading) {
    return null; // O un spinner si prefieres
  }

  return (
    <ThemeProvider>
      {/* BrandProvider va dentro de ThemeProvider para poder leer el tema
          activo (effectiveTheme) e inyectar la paleta light/dark correcta
          de la marca seleccionada via VITE_BRAND_NAME. */}
      <BrandProvider>
      <ToastProvider>
        <SettingsProvider>
          <BatchEditProvider>
            {/* Global session-expiry watcher: 5 min before the JWT lapses it
                shows a countdown modal to renew; if ignored, it logs out so no
                one keeps working into a dead session. No-op until authenticated. */}
            <SessionExpiryManager />
            {/* Puerta RGPD: bloquea la aplicación mientras queden documentos
                legales obligatorios sin aceptar en su versión vigente. */}
            <ConsentGateManager />
            {isNotFoundPage ? (
            <Routes>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          ) : isMicPage ? (
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/mic/:code" element={<MicPage />} />
              </Routes>
            </Suspense>
          ) : !isAuthPage ? (
          /* Por encima del router: el emparejamiento con el móvil sobrevive a
             navegar entre informes o salir del editor y volver a entrar. */
          <MicSessionProvider>
          <MainLayout>
            <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route
                path="/org/*"
                element={
                  <RoleProtectedRoute allowedRoles={['admin', 'super']}>
                    <OrgAdminPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Abiertas tambien a `admin`: aunque su home sea la organizacion,
                  puede redactar informes y editar plantillas institucionales. */}
              <Route 
                path="/reports" 
                element={
                  <PrivateRoute>
                    <ReportsDashboardPageV2 />
                  </PrivateRoute>
                } 
              />

              <Route 
                path="/reports/v2/edit" 
                element={
                  <PrivateRoute>
                    <KeyedReportEditPageV2 />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/reports/v2/edit/draft/:draftId" 
                element={
                  <PrivateRoute>
                    <KeyedReportEditPageV2 />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/reports/v2/edit/saved/:savedReportId" 
                element={
                  <PrivateRoute>
                    <KeyedReportEditPageV2 />
                  </PrivateRoute>
                } 
              />
              {/* Vista unificada de super: cuentas, usuarios e incidencias
                  agrupadas en pestañas dentro de una única página. */}
              <Route
                path="/super"
                element={
                  <RoleProtectedRoute allowedRoles={['super']}>
                    <SuperPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Rutas antiguas conservadas como redirecciones a la pestaña correspondiente. */}
              <Route path="/accounts" element={<Navigate to="/super?tab=accounts" replace />} />
              <Route path="/users" element={<Navigate to="/super?tab=users" replace />} />
              <Route path="/issues" element={<Navigate to="/super?tab=issues" replace />} />
              {/* Abierta tambien a `admin`: gestiona las plantillas de su
                  organizacion desde la misma pagina que los clinicos. */}
              <Route 
                path="/templates" 
                element={
                  <PrivateRoute>
                    <TemplatePage />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <PrivateRoute>
                    <SettingsPage />
                  </PrivateRoute>
                } 
              />

              {/* Tarifas por modalidad. Sin guard de rol: el backend decide si
                  el usuario puede EDITAR (`editable`) segun el `tariffOwner` de
                  su cuenta; verlas puede cualquiera, para saber con que precios
                  se le factura. */}
              <Route
                path="/billing"
                element={
                  <PrivateRoute>
                    <BillingSettingsPage />
                  </PrivateRoute>
                }
              />

              {/* Abierta tambien a `admin`: su home es la organizacion y
                  esta es su pagina de estadisticas. */}
              <Route
                path="/my-insights"
                element={
                  <PrivateRoute>
                    <RadiologistInsightsPage />
                  </PrivateRoute>
                }
              />
              {/* Historial de trabajo: informes enviados y borradores de los
                  miembros del ámbito. Un `user` solo ve los suyos; supervisor,
                  su grupo; admin/super, la cuenta entera con el árbol. */}
              <Route
                path="/group-reports"
                element={
                  <PrivateRoute>
                    <GroupReportsPage />
                  </PrivateRoute>
                }
              />
              {/* Abiertas tambien a `admin`: si puede redactar informes en
                  /reports/v2/edit, tambien puede dictar por lotes. */}
              <Route 
                path="/batch" 
                element={
                  <PrivateRoute>
                    <BatchReportingPageV2 />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/batch/edit/:batchId" 
                element={
                  <PrivateRoute>
                    <KeyedReportEditPageV2 />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/drafts/shared/:token" 
                element={
                  <PrivateRoute>
                    <SharedDraftPage />
                  </PrivateRoute>
                } 
              />
            </Routes>
            </Suspense>
          </MainLayout>
          </MicSessionProvider>
        ) : (
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-email" element={<VerifyTokenPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/azure/auth/callback" element={<AzureCallbackPage />} />
            <Route path="/launch" element={<LaunchPage />} />
            <Route
              path="/admin"
              element={
                <RoleProtectedRoute allowedRoles={['super']}>
                  <Suspense fallback={<LoadingSpinner />}>
                    <AdminPanelPage />
                  </Suspense>
                </RoleProtectedRoute>
              }
            />
            <Route 
              path="/" 
              element={
                isAuthenticated 
                  ? <Navigate to={getHomePath(user?.role)} replace /> 
                  : <Navigate to="/login" replace />
              } 
            />
          </Routes>
        )}
          </BatchEditProvider>
      </SettingsProvider>
    </ToastProvider>
      </BrandProvider>
    </ThemeProvider>
  );
}

export default App;